import clsx from 'clsx'
import { useMemo, type FC, type HTMLProps, type ReactElement } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import CompetencyStepper from './CompetencyStepper'
import { CompetencyStatus } from 'app/PemetaanKompetensiPage/components/AsesmenRefleksi/CompetencyCard'

import type { IAssessmentData } from 'types/content-platform'
import type { TProficiency } from 'types/competency-profile'

import PelatihanIcon from 'assets/icons/profil-kompetensi/simplified-icon-pelatihan.svg'
import OkeIcon from 'assets/icons/profil-kompetensi/thumb_up.svg'

export interface CompetencyCardProps extends HTMLProps<HTMLButtonElement> {
  name: string
  level: number
  minimumLevel: number
  icon?: ReactElement
  isLoading?: boolean
  isControl?: boolean
  competencyData?: IAssessmentData
}

type TCompetencyProficiency = {
  [key in TProficiency]: {
    label: string
    icon: ReactElement
    color: string
  }
}

export const CompetencyProficiency: TCompetencyProficiency = {
  under: {
    label: 'Perlu ditingkatkan.',
    icon: <PelatihanIcon width="16px" height="16px" />,
    color: 'text-[#C68B24]',
  },
  higher: {
    label: 'Selamat! Level lebih tinggi.',
    icon: <OkeIcon width="16px" height="16px" />,
    color: 'text-[#2E8A5C]',
  },
  equal: {
    label: 'Sudah baik',
    icon: <OkeIcon width="16px" height="16px" />,
    color: 'text-[#2E8A5C]',
  },
  nodata: {
    label: 'Level akan muncul di sini.',
    color: '',
    icon: null,
  },
}

const renderLabelStatus = (
  proficiency: TProficiency,
  isControl: boolean,
  data?: IAssessmentData
) => {
  if (
    !!data &&
    !isControl &&
    !['COMPLETED', 'NOT_CREATED'].includes(data?.status)
  ) {
    const notes =
      data?.status === 'DRAFTED'
        ? `(halaman ${data?.filledPages || 1}/${data?.totalPages}).`
        : ''
    return `${CompetencyStatus[data?.status]}${notes}`
  }
  return `${CompetencyProficiency[proficiency].label}`
}
const CompetencyCard: FC<CompetencyCardProps> = ({
  name,
  className,
  isLoading,
  minimumLevel,
  level,
  icon,
  isControl = true,
  competencyData,
  onClick,
}) => {
  const proficiency: TProficiency = useMemo(() => {
    if (level == 0) return 'nodata'
    if (level == minimumLevel) return 'equal'
    return level > minimumLevel ? 'higher' : 'under'
  }, [level, minimumLevel])

  return (
    <button
      onClick={onClick}
      className={clsx(
        'rounded-lg block w-full border-subdued border p-4 space-y-4',
        className,
        proficiency == 'nodata' && isControl ? 'bg-gray-5' : 'bg-white'
      )}
    >
      {isLoading ? (
        <Shimmer height={24} width={240} className="max-w-full" />
      ) : (
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-3.5">
            {icon}
            <Text
              variant="heading-md"
              color={
                proficiency == 'nodata' && isControl ? 'disabled' : 'default'
              }
              className="capitalize"
            >
              {name}
            </Text>
          </div>
          {isControl && (
            <Icon color={proficiency == 'nodata' ? 'disabled' : 'default'}>
              chevron_right
            </Icon>
          )}
        </div>
      )}
      <div
        className={clsx(
          'p-3 rounded-lg flex flex-col gap-4',
          isControl ? 'bg-gray-10' : 'bg-gray-0'
        )}
      >
        <CompetencyStepper step={level} />
        <div className="flex flex-row gap-2 items-center">
          {CompetencyProficiency[proficiency].icon}
          <Text variant="body-sm">
            {renderLabelStatus(proficiency, isControl, competencyData)}
          </Text>
        </div>
      </div>
    </button>
  )
}
export default CompetencyCard
